/**
 * Screening-vs-diagnostic billing-intent tracking, plus CPT modifier automation — `BACKLOG.md` M5's
 * own framing: "flagged as the single highest-ROI item here (a recurring compliance risk a generic
 * billing engine gets wrong)."
 *
 * THE COMPLIANCE RISK THIS MODULE EXISTS TO CATCH
 *
 * A colonoscopy scheduled and billed as a *screening* exam (no symptoms, average risk, preventive
 * intent) that finds and removes a polyp becomes, in the same encounter, a *diagnostic/therapeutic*
 * procedure by CPT definition — but the *patient's cost-sharing* is still governed by the screening
 * intent it started as. Two payer categories handle this differently, and billing it the wrong way is
 * exactly the "recurring compliance risk a generic billing engine gets wrong" M5's brief names:
 *
 *   - **Medicare**: a screening colonoscopy converted to diagnostic/therapeutic in the same encounter
 *     is billed with modifier **PT** ("colorectal cancer screening test converted to diagnostic test
 *     or other procedure") — this waives the deductible (though not necessarily coinsurance) rather
 *     than billing it as an ordinary diagnostic procedure with full cost-sharing.
 *   - **Commercial/ACA-regulated plans**: a screening colonoscopy — including one that converts to
 *     diagnostic/therapeutic — is billed with modifier **33** ("preventive service") to preserve the
 *     ACA's $0-cost-sharing preventive-service protection, which would otherwise be lost the moment
 *     the claim's own procedure code reads as diagnostic.
 *
 * Billing a converted screening exam with neither modifier — the generic-billing-engine failure mode
 * this module exists to prevent — routes the claim through ordinary diagnostic cost-sharing and
 * charges the patient a deductible/coinsurance the law/CMS policy says they should not owe, which is
 * both a real compliance exposure and the single most common colonoscopy billing complaint patients
 * raise.
 *
 * **UNREVIEWED.** The two-modifier shape (PT for Medicare, 33 for commercial) and the "screening
 * converted to diagnostic in the same encounter" trigger condition are widely cited CMS/payer coding
 * conventions, but this module has not verified them against a current, fetched CMS/AAPC primary
 * source this session — `BACKLOG.md`'s M5-M11 section header names CMS/AAPC specifically among the
 * sources blocked during research, and its own caveat ("numeric thresholds/billing-code details...are
 * flagged for re-verification before being hard-coded, not presented as settled fact") applies here
 * exactly as it does to `bowelPrep.ts`/`surveillanceIntervals.ts`. Per
 * `claude-kit/rules/invariants.md`'s "never hard-code an unverified clinical threshold as if it were
 * validated fact": build the real automation (this module), do not present its output as settled
 * billing guidance in a live claim until a certified coder or a fetched CMS/AAPC source confirms both
 * codes and the trigger condition, and that citation replaces this comment.
 */

/** Billing intent, recorded at the time the procedure is scheduled/performed —
 * `gastroenterologyRequests.ts`'s `createEndoscopyReportRequestSchema.indication`. `surveillance` is
 * its own intent, distinct from `diagnostic`: a surveillance exam (following a prior adenoma finding)
 * is neither an average-risk screening exam nor a symptom-driven diagnostic one, and billing it as
 * either would be wrong in a different way than the screening/diagnostic conversion this module's
 * header describes. */
export const PROCEDURE_INDICATIONS = ['screening', 'diagnostic', 'surveillance'] as const;
export type ProcedureIndication = (typeof PROCEDURE_INDICATIONS)[number];

export function isProcedureIndication(value: string): value is ProcedureIndication {
  return (PROCEDURE_INDICATIONS as readonly string[]).includes(value);
}

export const PAYER_CATEGORIES = ['medicare', 'commercial'] as const;
export type PayerCategory = (typeof PAYER_CATEGORIES)[number];

export interface BillingModifierResult {
  /** `null` when no modifier applies — an ordinary diagnostic/surveillance exam with no screening
   * intent to preserve, or a screening exam that found nothing and stayed purely preventive (still no
   * modifier needed; the base preventive CPT code already reflects that on its own). */
  readonly modifier: 'PT' | '33' | null;
  readonly reason: string;
}

/**
 * UNREVIEWED — see this module's header. `foundInterventionRequiringFinding` is true when the exam
 * found something requiring biopsy, polypectomy, or other therapeutic action (i.e., there is at least
 * one polyp on the report) — the "converted to diagnostic/therapeutic" trigger.
 */
export function modifierForColonoscopy(
  indication: ProcedureIndication,
  foundInterventionRequiringFinding: boolean,
  payerCategory: PayerCategory,
): BillingModifierResult {
  if (indication !== 'screening') {
    return {
      modifier: null,
      reason: `${indication} exam — not a screening encounter, no screening-preservation modifier applies`,
    };
  }

  if (!foundInterventionRequiringFinding) {
    return {
      modifier: null,
      reason:
        'Screening exam with no polypectomy/biopsy — bill the preventive code alone, no conversion modifier needed',
    };
  }

  if (payerCategory === 'medicare') {
    return {
      modifier: 'PT',
      reason:
        'Medicare: screening colonoscopy converted to diagnostic/therapeutic in the same encounter — modifier PT waives the deductible',
    };
  }

  return {
    modifier: '33',
    reason:
      'Commercial/ACA-regulated plan: screening colonoscopy converted to diagnostic/therapeutic — modifier 33 preserves $0 preventive cost-sharing',
  };
}
